import React, { CSSProperties, useEffect, useMemo, useState } from 'react'
import { useSessionStorage } from 'react-use'
import clsx from 'clsx'

import { Alignment } from '@makyoapp/makyo-sdk/models/AppBuilder'
import {
  ColumnConditionValue,
  ConditionValue,
  IActionRepeaterValue,
  TableColumnListColumnValue,
  TableColumnListValue,
  TypographyGroupValue,
} from '@makyoapp/makyo-common'
import {
  BaseComponent,
  CornerRadius,
  getTypographyTokenStyle,
  HoverEffectVariant,
  HoverIntensity,
  SearchBar,
} from '@makyoapp/makyo-tw-ui'

import { CoreComponentAction, CoreComponentInfos, CoreComponentTableData } from '@/core/models/app-components/types'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useCheckVisibilityV2 } from '@/hooks/__project__/useCheckVisibilityV2'
import { useCompTableData } from '@/hooks/__project__/useCompTableData'
import { ComponentSettingKey, UserColumnFilterValue } from '@/shared/builder'
import { parseSpacing } from '@/shared/builder-utils'
import { SpacingInputValue } from '@/shared/builder/types/spacing-input'
import { MAX_ROWS_LIMIT } from '@/shared/constants'
import { useDebounceInput } from '@/shared/makyo-hooks'
import { getSafely } from '@/shared/safely'

import { useCompPagination } from '../../hooks/useCompPagination'
import { CompFilterV2 } from '../../shared/CompFilterV2'
import { CompPagination } from '../../shared/CompPagination'
import { CompVariableText } from '../../shared/CompVariableText'
import { CompDataTableCoreProps } from './comp'

export interface CompDataTableWrapperCoreProps {
  infos: CoreComponentInfos
  action: CoreComponentAction
  tableData: CoreComponentTableData
  listChecked: any
  setListChecked: (checked: any) => void
  children: (props: CompDataTableCoreProps) => React.ReactNode
}

/**
 * CompDataTableWrapperCore
 *
 */
export const CompDataTableWrapperCore = React.memo(
  ({
    infos: { settings, component, containerClient, detailRowIndex, detailTableId, currentParentBg, rowId: detailRowId },
    action: { simulateAction },
    tableData: { dbTable },
    listChecked,
    setListChecked,
    children,
  }: CompDataTableWrapperCoreProps) => {
    const { checkVisibility } = useCheckVisibilityV2()

    const compId = component._id
    const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()

    //   ========================= data =========================
    const dataSource = settings?.[ComponentSettingKey.TableDataSource] as TableColumnListValue
    const columns = getSafely(dataSource, ['columns'], [] as TableColumnListColumnValue[])
    const pageSizes = [5, 10, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100]

    const datasetLimit = settings[ComponentSettingKey.TableDataSetLimit]
    const isLimited = datasetLimit?.enabled
    const limit = datasetLimit?.value ?? 0
    const maxPerItems = isLimited ? limit : MAX_ROWS_LIMIT

    const numRows = (settings[ComponentSettingKey.TableDesignNumRows] as number) ?? 1

    const maxItems = numRows > 0 ? numRows : 10

    // pagination state
    const { pagination, setPagination } = useCompPagination({
      compId,
      defaultPerPage: Math.min(maxItems, maxPerItems),
    })

    // data filter & sort settings
    const filterSetting = settings[ComponentSettingKey.TableDataFilter] as ColumnConditionValue
    const sortType = settings[ComponentSettingKey.TableDataSortType] as string
    const sortBy = settings[ComponentSettingKey.TableDataSortBy] as string

    // sort state
    const [sortSetting, setSortSetting] = useState({ sortType, sortBy })

    useEffect(() => {
      setSortSetting({ sortType, sortBy })
    }, [sortType, sortBy])

    // search
    const [debouncedSearch, , , currentSearch, setCurrentSearch] = useDebounceInput('')

    // user filter state
    const [userFilter, setUserFilter] = useSessionStorage(`userFilter-${compId}`, {})

    // get initial rows
    const [{ rows, filteredRowsCount, allRowsCount }, isLoading] = useCompTableData({
      table: dbTable,
      componentId: component._id,
      pagination,
      filterSetting,
      sortSetting,
      search: debouncedSearch,
      userFilter,
    })

    const totalPages = useMemo(() => {
      return Math.ceil(filteredRowsCount / pagination.perPage)
    }, [filteredRowsCount, pagination.perPage])

    // user (manual) filter settings
    const userFilterSetting: Array<UserColumnFilterValue> = useMemo(() => {
      let stg = settings[ComponentSettingKey.TableBehaviorUserFilter] as UserColumnFilterValue[]
      return Array.isArray(stg) ? stg : []
    }, [settings])

    // parsed table settings
    const tableSettings = useMemo(
      () => ({
        inProgress: false,
        paginated: getSafely(settings, [ComponentSettingKey.TableBehaviorPagination], false),
        stripped: getSafely(settings, [ComponentSettingKey.TableDesignStrippedRow], false),
        withHeader: getSafely(settings, [ComponentSettingKey.TableDesignHeader], false),
        verticalBorder: getSafely(settings, [ComponentSettingKey.TableDesignVerticalBorder], false),
        horizontalBorder: getSafely(settings, [ComponentSettingKey.TableDesignHorizontalBorder], false),
        checkbox: getSafely(settings, [ComponentSettingKey.TableBehaviorRowSelection], false),
        sorting: getSafely(settings, [ComponentSettingKey.TableBehaviorSorting], false),
        itemsPerPage: getSafely(settings, [ComponentSettingKey.TableDataItemsPerPage], [5]),
      }),
      [settings]
    )

    // ========================= design =========================

    // base
    const width = getSafely(settings, [ComponentSettingKey.TableDesignWidth], ['100%'])[0]
    const alignment = getSafely(settings, [ComponentSettingKey.TableDesignComponentHAlign], 'parent') as Alignment
    const margin = settings[ComponentSettingKey.TableDesignMargin]
    const padding = settings[ComponentSettingKey.TableDesignPadding]

    // data-table
    const footerAlignment = getSafely(settings, [ComponentSettingKey.TableDesignFooterHAlign], 'end')
    const titleAlignment = getSafely(settings, [ComponentSettingKey.TableDesignTitleHAlign], 'start')
    const paginationAlign = getSafely(settings, [ComponentSettingKey.TableDesignPaginationHAlign], 'end')
    const titleMargin = parseSpacing(settings[ComponentSettingKey.TableDesignTitleMargin] as SpacingInputValue)

    const typoSetting = settings?.[ComponentSettingKey.TableDesignTypography] as TypographyGroupValue
    const headlineTypo = typoSetting?.[ComponentSettingKey.TableDesignTitleTypography]
    const rowHeaderTypo = typoSetting?.[ComponentSettingKey.TableDesignRowHeaderTypography]
    const rowItemTypo = typoSetting?.[ComponentSettingKey.TableDesignRowItemTypography]
    const footerTypo = typoSetting?.[ComponentSettingKey.TableDesignFooterTypography]

    const headlineTypoStyle = getTypographyTokenStyle(headlineTypo || { typography: 'title-large' })
    const rowHeaderTypoStyle = getTypographyTokenStyle(rowHeaderTypo || { typography: 'title-medium' })
    const rowItemTypoStyle = getTypographyTokenStyle(rowItemTypo || { typography: 'body-medium' })
    const footerTypoStyle = getTypographyTokenStyle(footerTypo || { typography: 'body-small' })

    const outlineBorderWidth = getSafely(settings, [ComponentSettingKey.TableDesignOutlineBorderWidth], [0])[0]
    const tableShape = settings?.[ComponentSettingKey.TableDesignRoundedCorner] as CornerRadius

    const headerFooterColor = settings?.[ComponentSettingKey.TableDesignHeaderFooterColor]
    // const cc_color = `var(--md-sys-color-${headerFooterColor || 'secondary-container'})`
    const oddBodyColor = settings?.[ComponentSettingKey.TableDesignOddRowColor]
    const evenBodyColor = settings?.[ComponentSettingKey.TableDesignEvenRowColor]
    // const titleColor = settings?.[ComponentSettingKey.TableDesignTitleColor]
    const borderColor = settings?.[ComponentSettingKey.TableDesignBorderColor]
    const searchColor = settings?.[ComponentSettingKey.TableDesignSearchColor]
    const searchColumnFilterColor = settings?.[ComponentSettingKey.TableDesignSearchColumnFilterColor]

    const textLines = settings?.[ComponentSettingKey.TableDesignTextLines]
    const textLinesValue = textLines?.[0] ?? 1

    // const textTitleAlignment = titleAlignment === 'center' ? 'center' : titleAlignment === 'end' ? 'right' : 'left'
    // const adaptiveTextColor = getAdaptiveColor(headerFooterColor, 0.98, undefined, undefined)
    // const adaptiveTextColorBody = getAdaptiveColor(oddBodyColor, 0.98, undefined, undefined)

    const showPagination = settings?.[ComponentSettingKey.TableDesignShowPagination]
    const showColumnToggle = settings?.[ComponentSettingKey.TableDesignShowColumnToggle] ?? false
    const showGlobalFilter = settings?.[ComponentSettingKey.TableDesignShowGlobalFilter] ?? false

    // search box
    const searchAlign = settings[ComponentSettingKey.TableDesignSearchAlignment] ?? 'center'
    const searchBgColor = settings?.[ComponentSettingKey.TableDesignSearchBgColor] ?? 'primary-90'
    const searchMargin = parseSpacing(settings[ComponentSettingKey.TableDesignSearchMargin] as SpacingInputValue, true)
    const searchCornerRadius = settings[ComponentSettingKey.TableDesignSearchCorner] as CornerRadius
    const searchWidth = settings[ComponentSettingKey.TableDesignSearchWidth]?.[0] ?? 100
    const searchWidthValue = Number(String(searchWidth).replace(/[^0-9.]/g, '')) || 100
    const searchWidthUnit = String(searchWidth).replace(/[0-9.]/g, '') || '%'

    // add button
    const addButtonColor = settings[ComponentSettingKey.TableDesignAddButtonColor] ?? 'primary-20'
    const addButtonCornerRadius = settings[ComponentSettingKey.TableDesignAddButtonCornerRadius] as CornerRadius
    const addButtonPosition = settings[ComponentSettingKey.TableDesignAddButtonPosition] ?? 'inline'

    // const hoverEffect = settings[ComponentSettingKey.TableDesignHoverEffect]

    const tableHeaderStyles: CSSProperties = {
      backgroundColor: 'var(--_colored-container-color)',
      color: 'var(--_colored-bg-text-color)',
    }

    const tableRowItemStyles: CSSProperties = {
      backgroundColor: 'var(--_colored-alt-container-color)',
      color: 'var(--_colored-alt-bg-text-color)',
    }

    // ========================= action =========================
    const handleItemClick = (rowId: string) => {
      simulateAction({
        actions: settings[ComponentSettingKey.TableActionItemClick] as IActionRepeaterValue[],
        rowId,
        tableId: dbTable.settings._id,
      })
    }

    const handleOnCheck = (rowId: string, isChecked: boolean) => {
      setListChecked((maybePrev: any) => {
        const prev = {
          id: compId,
          items: maybePrev?.id === compId ? [...maybePrev.items] : [],
          icon: 'menu',
          action: settings[ComponentSettingKey.TableActionItemSelection] as IActionRepeaterValue,
          extraActionProps: {
            tableId: dbTable.settings._id,
          },
        }
        if (prev.items.includes(rowId)) {
          prev.items = prev.items.filter((pId) => pId !== rowId)
        } else {
          prev.items = [...prev.items, rowId]
        }
        return prev
      })
    }

    const handleCheckAll = (rowIds: string[]) => {
      setListChecked({
        id: compId,
        items: rowIds,
        icon: 'menu',
        action: settings[ComponentSettingKey.TableActionItemSelection] as IActionRepeaterValue,
        extraActionProps: {
          tableId: dbTable.settings._id,
        },
      })
    }

    const handleSortChange = (sortSetting: { sortBy: string | null; sortType: 'asc' | 'desc' | null }) => {
      setSortSetting({
        sortBy: sortSetting.sortBy || '',
        sortType: sortSetting.sortType || '',
      })
    }

    const handleClick = () => {
      openComponentSettings(component)
      if (isDevelopmentMode) return
    }

    const handleAddButtonClick = () => {
      simulateAction({
        actions: (settings[ComponentSettingKey.TableActionAddButtonClick] ?? []) as IActionRepeaterValue[],
        tableId: dbTable.id,
      })
    }

    // ========================= behavior =========================
    const withSearch = Boolean(settings[ComponentSettingKey.TableBehaviorUserSearch])

    const withAddButton = settings[ComponentSettingKey.TableBehaviorAddButtonShow] as boolean
    const addButtonVisibility = settings[ComponentSettingKey.TableBehaviorAddButtonVisibility] as
      | ConditionValue
      | undefined

    const showAddButton =
      withAddButton &&
      checkVisibility({
        visibility: addButtonVisibility,
        layerTableId: dbTable.id,
      })

    // ========================= elements =========================

    // comp title
    const title = settings?.[ComponentSettingKey.TableDataCompTitle] ?? []

    const hasDataInNode = (node: any): boolean => {
      if (!node) return false

      if (Array.isArray(node)) {
        return node.some((item) => hasDataInNode(item))
      }

      if (typeof node === 'object') {
        if (node.text && node.text.trim() !== '') return true

        if (node.value && node.value.trim() !== '') return true

        if (node.children) {
          return hasDataInNode(node.children)
        }
      }

      return false
    }

    const hasLabel = hasDataInNode(title)

    const renderCompTitle = () => (
      <h4
        className='flex items-center'
        style={
          {
            justifyContent: titleAlignment,
            font: headlineTypoStyle.font,
            letterSpacing: headlineTypoStyle.letterSpacing,
            textTransform: headlineTypoStyle.textTransform,
            color: `var(--color-on-${currentParentBg})`,
            margin: `${titleMargin.paddingTop} ${titleMargin.paddingInlineEnd} ${titleMargin.paddingBottom} ${titleMargin.paddingInlineStart}`,
          } as React.CSSProperties
        }
      >
        <CompVariableText
          nodes={title}
          containerClient={containerClient}
          tableId={detailTableId}
          rowIndex={detailRowIndex}
          rowId={detailRowId}
          textProps={{ style: { whiteSpace: 'pre-wrap' } }}
        />
      </h4>
    )

    const renderCustomPagination = () => (
      <div className={clsx('flex w-full py-4 pagination')} style={{ justifyContent: paginationAlign }}>
        <CompPagination
          totalPages={totalPages}
          perPage={pagination.perPage}
          page={pagination.page}
          onChange={setPagination}
          backgroundColor={currentParentBg}
        />
      </div>
    )

    if (rows.length === 0) return null

    return (
      <BaseComponent
        id={component._id}
        width={width}
        alignment={alignment}
        margin={margin}
        padding={padding}
        backgroundColor={currentParentBg}
        onClick={handleClick}
      >
        {isLoading ? (
          <div>Loading...</div>
        ) : (
          children({
            table: dbTable,
            rowItems: rows,
            columns,
            tableHeaderStyles,
            tableRowItemStyles,
            hasLabel,
            pageSizes,
            isPaginated: tableSettings.paginated && columns.length > 0,
            tableSettings,
            // customTableStyles,
            footerAlignment,
            renderCompTitle,
            rowItemTypoStyle,
            onClick: handleItemClick,
            onItemSelect: handleOnCheck,
            listChecked,
            onSelectAll: handleCheckAll,
            headlineTypoStyle,
            rowHeaderTypoStyle,
            footerTypoStyle,
            headerFooterColor,
            oddBodyColor,
            evenBodyColor,
            borderColor,
            showPagination,
            showColumnToggle,
            showGlobalFilter,
            containerClient,
            outlineBorderWidth,
            tableShape,
            searchCorner: searchCornerRadius,
            searchBoxWidth: `${searchWidthValue}${searchWidthUnit}`,
            parentBackgroundColor: currentParentBg as string,
            spacingTable: margin,
            searchColor,
            searchColumnFilterColor,
            maxLines: textLinesValue,
            renderCustomPagination,
            setSortSetting: handleSortChange,
            maxItems,
            headlineTypo,
            rowHeaderTypo,
            rowItemTypo,
            footerTypo,
            // hoverEffect: hoverEffect as HoverEffectVariant,
            // hoverIntensity: 'md' as HoverIntensity,
            // add button
            showAddButton,
            addButtonColor,
            addButtonCornerRadius,
            addButtonPosition,
            handleAddButtonClick,
          })
        )}
      </BaseComponent>
    )
  }
)

CompDataTableWrapperCore.displayName = 'CompDataTableWrapperCore'
